import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react"
import { MdClose } from "react-icons/md"
import { MobergColumn, MobergRow, MobergHeader, MobergButtonShape, MobergIconSize, MobergFontSize, MobergTheme, MobergButton, MobergBoxShadow, getColors, MobergFontFamily } from "../../../Moberg"
import { useModalProvider } from "../../../Providers/ModalProvider"
import { ScreenshotEntry, useScreenshotProvider } from "../../../Providers/ScreenshotsProvider"
import { FileCopyOutlined, FileDownloadOutlined, GppGood } from "@mui/icons-material"
import { currentPatientFileInfoAtom } from "../../../Pages/Data/Visualize/DataReview/Atoms/PatientFile"
import { useRecoilValue } from "recoil"
// @ts-ignore - ModernMUITable doesn't have TypeScript declarations
import ModernMUITable from '../../../Components/ModernMUITable.js'
import ConfirmationModal from "../../../Components/ConfirmationModal/ConfirmationModal"
import { useMobergTheme } from "../../../Providers/MobergThemeProvider"
import { useTableContext } from "../../../Providers/TableProvider"

export const ScreenshotViewer = () => {
    const { setToastProps } = useTableContext()
    const { screenshots, remove, saveToDevice } = useScreenshotProvider()
    const [selectedScreenshot, setSelectedScreenshot] = useState<ScreenshotEntry | undefined>()
    const { createModal, close } = useModalProvider()

    // Make sure the existing selected screenshot exists
    useEffect(() => {
        if (!selectedScreenshot) {
            return
        }

        const foundScreenshot = screenshots.find(screenshot => selectedScreenshot?.id === screenshot.id)
        if (!foundScreenshot) {
            if (screenshots.length > 0) {
                setSelectedScreenshot(screenshots[0])
            } else {
                setSelectedScreenshot(undefined)
            }
        }
    }, [screenshots, selectedScreenshot])
        
    // If there isn't a screenshot selected, make sure one is if there is one available
    useEffect(() => {
        if (!selectedScreenshot && screenshots.length > 0) {
            setSelectedScreenshot(screenshots[0])
        }
    }, [screenshots, selectedScreenshot])

    const removeScreenshot = () => {
        if (selectedScreenshot) {
            remove(selectedScreenshot.id)
        }
    }

    const handleSaveToDevice = () => {
        if (!selectedScreenshot) {
            return
        }
        
        if (selectedScreenshot.metadata.isDeidentified) {
            saveToDevice(selectedScreenshot)
        } else {
            createModal(<ConfirmationModal 
                title={"Save PHI?"}
                description={"This screenshot contains PHI and it will be saved to your computer. It is recommended to create a de-identified screenshot instead so it can be securely shared."}
                confirmButton={{ text: "Save PHI to computer", theme: MobergTheme.BLUE, onClick: () => saveToDevice(selectedScreenshot) }}
            />)
        }
    }

    const copyToClipboard = async (screenshot: ScreenshotEntry) => {
        const base64Data = screenshot.imageData.split(',')[1]
        const byteCharacters = atob(base64Data)
        const byteArray = new Uint8Array(byteCharacters.length)

        for (let i = 0; i < byteCharacters.length; i++) {
            byteArray[i] = byteCharacters.charCodeAt(i)
        }

        const blob = new Blob([byteArray], { type: 'image/png' })

        let toastMessage, severity

        try {
            await (navigator.clipboard as any).write([new ClipboardItem({ 'image/png': blob })])
            toastMessage = "Screenshot was copied to your clipboard."
            severity = "success"
        } catch (error) {
            toastMessage = `${error}`
            severity = "error"
        }

        setToastProps({ isOpen: true, message: toastMessage, severity })
    }

    const handleCopyToClipboard = () => {
        if (!selectedScreenshot) {
            return
        }
        
        if (selectedScreenshot.metadata.isDeidentified) {
            copyToClipboard(selectedScreenshot)
        } else {
            createModal(<ConfirmationModal 
                title={"Copy PHI?"}
                description={"This screenshot contains PHI, are you sure? It is recommended to create a de-identified screenshot instead so it can be securely shared."}
                confirmButton={{ text: "Yes, copy PHI", theme: MobergTheme.BLUE, onClick: () => copyToClipboard(selectedScreenshot) }}
            />)
        }
    }

    return (
        <MobergColumn style={{ padding: "16px", width: "98vw", paddingTop: "8px", maxHeight: "98vh" }} gap="8px">
            <MobergRow horizontalAlign="space-between">
                <MobergHeader style={{ fontSize: MobergFontSize.XX_LARGE }}>Export Screenshot</MobergHeader>
                <MobergButton onClick={close} shape={MobergButtonShape.SQUARE}>
                    <MdClose size={MobergIconSize.REGULAR} />
                </MobergButton>
            </MobergRow>

            <MobergRow gap="32px">
                {selectedScreenshot && (
                    <MobergColumn gap="8px" style={{ flex: 1 }}>
                        <MobergRow style={{ borderRadius: "6px", height: "80vh" }}>
                            <img src={selectedScreenshot.imageData}
                                alt={selectedScreenshot.metadata.fileName}
                                style={{ flex: "1 1 0", minHeight: 0, minWidth: 0, width: "100%", height: "100%", objectFit: "contain", boxShadow: MobergBoxShadow.LOW }} />
                        </MobergRow>

                        <MobergRow>
                            <MobergRow horizontalAlign="left">
                                <MobergButton theme={MobergTheme.BLUE} onClick={handleSaveToDevice}>
                                    <span><FileDownloadOutlined /></span>
                                    Save to computer
                                </MobergButton>

                                <MobergButton theme={MobergTheme.BLACK} onClick={handleCopyToClipboard}>
                                    <span><FileCopyOutlined /></span>
                                    Copy to clipboard
                                </MobergButton>

                            </MobergRow>
                            <MobergButton theme={MobergTheme.RED} onClick={removeScreenshot}>
                                Remove screenshot
                            </MobergButton>
                        </MobergRow>

                    </MobergColumn>
                )}

                {!selectedScreenshot && (
                    <MobergRow style={{ flex: 1 }} horizontalAlign="center">
                        <MobergColumn>
                            <MobergHeader> No screenshot selected </MobergHeader>
                        </MobergColumn>
                    </MobergRow>
                )}

                <MobergColumn style={{ width: "500px", position: "relative", height: "calc(100vh - 270px)", overflow: "hidden" }} gap="8px">
                    <MobergHeader>All screenshots</MobergHeader>
                    <ScreenshotsTableView 
                        selectedScreenshot={selectedScreenshot}
                        setSelectedScreenshot={setSelectedScreenshot} 
                    />
                </MobergColumn>

            </MobergRow>
        </MobergColumn>
    )
}

type ScreenshotsTableViewProps = {
    selectedScreenshot: ScreenshotEntry | undefined
    setSelectedScreenshot: Dispatch<SetStateAction<ScreenshotEntry | undefined>>
}

type TableRowInfo = {
    imageData: string
    time: Date
    type: string
    isDeidentified: boolean
    mrn: string
    screenshot: ScreenshotEntry
}

type TableCellInfo = {
    row: TableRowInfo
}

export const ScreenshotsTableView: React.FC<ScreenshotsTableViewProps> = ({ selectedScreenshot, setSelectedScreenshot }) => {
    const { screenshots } = useScreenshotProvider()
    const [searchValue, setSearchValue] = useState("")
    const cleanSearchValue = searchValue.trim().toLowerCase()
    const { timeZone } = useRecoilValue(currentPatientFileInfoAtom)
    const { colorScheme } = useMobergTheme()

    const dateFormatter = useMemo(() => {
        const options: Intl.DateTimeFormatOptions & { hourCycle: string, timeZoneName: string} = {
            timeZone,
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            timeZoneName: "short",
            hourCycle: "h23"
        }

        return new Intl.DateTimeFormat("en-us", options)
    }, [timeZone])

    const screenshotTableData = screenshots
        .filter((screenshot: ScreenshotEntry) => (
            !cleanSearchValue ||
            (
                screenshot.metadata.fileName.toLowerCase().includes(cleanSearchValue) ||
                screenshot.metadata.displayType.toLowerCase().includes(cleanSearchValue)
            )
        ))
        .map((screenshot: ScreenshotEntry) => ({
            id: screenshot.id,
            imageData: screenshot.imageData,
            time: screenshot.metadata.timeGenerated,
            type: screenshot.metadata.displayType,
            isDeidentified: screenshot.metadata.isDeidentified,
            mrn: screenshot.metadata.mrn,
            screenshot
        }))


    const handleSelectedRow = useCallback((row: TableRowInfo) => {
        setSelectedScreenshot(row.screenshot)
    }, [setSelectedScreenshot])

    const screenshotTableColumns = useMemo(() => [
        {
            field: "time", headerName: "Time", visible: true, minWidth: 50, flex: 0,
            renderCell: (info: TableCellInfo) => {
                const formattedDate = dateFormatter.format(info.row.time)
                const parts = formattedDate.split(", ")
                const [monthAndDay, year, time] = parts
                return (
                    <MobergRow expand={false} style={{ height: "100%" }}>
                        <MobergColumn style={{ fontSize: MobergFontSize.SMALL, lineHeight: "1.6" }}>
                            <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {monthAndDay}, {year}
                            </div>
                            <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {time}
                            </div>
                        </MobergColumn>
                    </MobergRow>
                )
            }
        },
        {
            field: "mrn", headerName: "MRN", visible: true, flex: 0,
            renderCell: (info: TableCellInfo) => (
                <span>{info.row.mrn}</span>
            )
        },
        {
            field: "name", headerName: "Type", visible: true, flex: 1,
            renderCell: (info: TableCellInfo) => (
                <span>{info.row.type}</span>
            )
        },
        {
            field: "isDeidentified", headerName: "PHI", visible: true, minWidth: 50, flex: 0,
            renderCell: (info: TableCellInfo) => (
                <MobergRow style={{ height: "100%" }}>{info.row.isDeidentified 
                    ? <span style={{ color: getColors(MobergTheme.BLUE).main}}><GppGood /></span>
                    : <span style={{color: getColors(MobergTheme.RED).main}}>Exposed</span>}
                </MobergRow>
            )
        },
    ], [dateFormatter])

    const blueTheme = getColors(MobergTheme.BLUE)
    const selectedBackgroundColor = blueTheme.noFill.active
    const darkModeBackgroundColor = blueTheme.dark?.active
    const selectedBorderColor = blueTheme.main

    return (
        <ModernMUITable
            tableData={screenshotTableData}
            columns={screenshotTableColumns}
            checkboxSelection={false}
            isRowSelectable={() => true}
            disableRowSelectionOnClick={false}
            handleOnRowClick={handleSelectedRow}
            onSearchValueChanged={(searchValue: string) => setSearchValue(searchValue)}
            tableToolbarProps={{
                searchInput: { isShow: false },
                columnButton: { isShow: false }
            }}
            height="100%"
            scrollbarAlwaysVisible={true}
            disableVirtualization={false}
            getRowClassName={(info: TableCellInfo) => info.row.screenshot.id === selectedScreenshot?.id ? 'selected-row' : ''}
            overrideStyles={{
                "& .MuiDataGrid-row.selected-row": {
                    backgroundColor: `${colorScheme === 'dark' ? darkModeBackgroundColor : selectedBackgroundColor} !important`,
                    border: `1px solid ${selectedBorderColor}`,
                    '&:hover': { backgroundColor: selectedBackgroundColor },
                },
                '& .MuiDataGrid-cell:not(:last-child)': { // This must override the base .MuiDataGrid-cell:not(:last-child) selector
                    fontFamily: MobergFontFamily.REGULAR,
                    fontSize: '1rem',
                    color: 'var(--color-table-cell-text) !important',
                    borderColor: 'var(--color-table-border)',
                },
                '& .MuiDataGrid-cell:last-child': { // This must override the base .MuiDataGrid-cell:last-child selector
                    fontFamily: MobergFontFamily.REGULAR,
                    fontSize: '1rem',
                    color: 'var(--color-table-cell-text) !important',
                },
                "& .MuiDataGrid-row": { // must be overridden to prevent dark theme from overriding the background color
                    minHeight: "40px !important",
                },
                '& .MuiDataGrid-cell' : { // needed to override dark mode barkground color
                    backgroundColor: "transparent !important"
                },
                '& .MuiDataGrid-cell:focus': { // Hide the blue focus box when clicking on a cell
                    outline: 'none',
                },
                '& .MuiDataGrid-cell:focus-within': { // Hide the blue focus box when clicking on a cell
                    outline: 'none',
                },
                '& .MuiDataGrid-toolbar': { // NO TOOLBAR
                    height: 0,
                    padding: 0,
                }
            }}
        />
    )
}
